import React, { useMemo } from 'react';
import useFetchEmployees, { EmployeeResponse } from '@sekoia/shared/requests/atlas/useFetchEmployees';
import { Skeleton } from '@mui/material';
import ProfileSelector, { IdAndName } from './ProfileSelector';
import {
  OrganisationAffiliationResponse,
  ResidentAffiliationResponse,
} from '@sekoia/shared/requests/nswagClients/AtlasClient';

type EmployeeSelectorProps = {
  label: string;
  noOptionsText: string;
  placeholder?: string;
  handleOnEmployeeChange?: (employee: EmployeeResponse | undefined) => void;
  handleOnEmployeesChange?: (employees: EmployeeResponse[]) => void;
  employeesToList?: EmployeeResponse[];
  value?: string | string[] | null;
  multiple?: boolean;
  excludeEmployeeIds?: string[];
  organisationsToInclude?: string[];
  required?: boolean;
  residentIdToFilterBy?: string;
};

const EmployeeSelector: React.FC<React.PropsWithChildren<EmployeeSelectorProps>> = (props) => {
  const {
    label,
    noOptionsText,
    placeholder,
    handleOnEmployeeChange,
    handleOnEmployeesChange,
    employeesToList,
    value,
    multiple = false,
    excludeEmployeeIds,
    organisationsToInclude,
    required = false,
    residentIdToFilterBy,
  } = props;
  const { data: employees, fetchStatus: employeesFetchStatus } = useFetchEmployees(
    [],
    false,
    employeesToList === undefined,
  );

  const profiles = useMemo(() => {
    const idAndNames: IdAndName[] = [];
    const employeeList = employeesToList ?? employees;

    if (!employeeList) return idAndNames;

    employeeList.forEach((e) => {
      let shouldInclude = !organisationsToInclude && !residentIdToFilterBy;

      if (organisationsToInclude) {
        shouldInclude =
          shouldInclude ||
          e.affiliations.some((affiliation) => {
            if (affiliation instanceof OrganisationAffiliationResponse) {
              return organisationsToInclude.includes(affiliation.organisationId);
            } else return false;
          });
      }

      if (residentIdToFilterBy) {
        const hasResidentAffiliation = e.affiliations.some(
          (affiliation) =>
            affiliation instanceof ResidentAffiliationResponse && affiliation.residentId === residentIdToFilterBy,
        );
        shouldInclude = shouldInclude || hasResidentAffiliation;
      }

      if (shouldInclude && !excludeEmployeeIds?.includes(e.globalId)) {
        idAndNames.push({
          name: `${e.firstname} ${e.lastname} `,
          id: e.globalId,
          customerId: e.customerId,
        });
      }
    });

    return idAndNames;
  }, [employees, employeesToList, excludeEmployeeIds, organisationsToInclude, residentIdToFilterBy]);

  const handleOnProfileChange = (profile: IdAndName | undefined) => {
    if (!profile) return;
    const employeesObject = employeesToList ?? employees;
    const employee = employeesObject?.find((e) => e && e.globalId === profile.id);
    handleOnEmployeeChange && handleOnEmployeeChange(employee);
  };
  const handleOnProfilesChange = (profiles: IdAndName[]) => {
    const employeesObject = employeesToList ?? employees;
    const employeesSelected = employeesObject?.filter((e) => profiles.map((i) => i.id).includes(e.globalId));
    handleOnEmployeesChange && handleOnEmployeesChange(employeesSelected ?? []);
  };

  if (employeesFetchStatus === 'fetching') {
    return <Skeleton width={'100%'} variant="rectangular" height={56} />;
  }

  return (
    <ProfileSelector
      label={label}
      noOptionsText={noOptionsText}
      profiles={profiles}
      handleOnProfileChange={handleOnProfileChange}
      handleOnProfilesChange={handleOnProfilesChange}
      multiple={multiple}
      placeholder={placeholder}
      required={required}
      value={value}
    />
  );
};

export default EmployeeSelector;
EmployeeSelector.displayName = 'EmployeeSelector';
